import { formatDate, formatDateTime } from '../../../utils/parsing';
import { ColumnType, getRowValue } from './Table';

export type FiltersType = {
    columnKey: string,
    key: string,
    label: string,
    getDescription: (testValue: any) => string,
    test: (row: any, testValue: any) => boolean,
    options?: {
        label: string,
        value: string
    }[],
    isMultiSelect?: boolean,
    exclusive?: boolean,
    applyDirectly?: boolean
}[];

type FormatValueType = { formatValue: (v: string) => string | number };

// TEST GENERATORS
function equals(column: ColumnType) {
    const options = column.options?.length
        ? [
            { label: '', value: '' },
            ...column.options.map((opt) => (
                typeof opt === 'string' ? { label: opt, value: opt } : opt
            ))
        ]
        : undefined;

    return ({
        columnKey: column.key,
        key: `${column.key}-equals`,
        label: options ? column.name : 'Lika med',
        applyDirectly: column.applyDirectly,
        getDescription: (testValue: string | number | boolean) => { // testValue can be boolean
            if (typeof testValue === 'boolean') {
                return testValue ? column.name : `Inte ${column.name.toLowerCase()}`;
            }
            if (options) {
                const optLabel = options.find((o) => String(o.value) === String(testValue))?.label;
                return optLabel
                    ? `${column.name}: ${optLabel}`
                    : `${column.name} är "${String(testValue)}"`;
            }
            return `${column.name} är "${String(testValue)}"`;
        },
        // Prefer raw row[key] when options map to stored values (getValue is display-only)
        test: (row: any, testValue: string) => (
            options && column.key in row
                ? row[column.key] === testValue
                : getRowValue({ column, row }) === testValue
        ),
        options
    });
}

function hasValue(column: ColumnType) {
    return ({
        columnKey: column.key,
        key: `${column.key}-hasValue`,
        label: 'Har värde',
        applyDirectly: column.applyDirectly,
        getDescription: (testValue: string) => ( // testValue is boolean
            testValue
                ? `${column.name} har värde`
                : `${column.name} saknar värde`
        ),
        test: (row: any, testValue: string) => {
            const value = getRowValue({ column, row });
            return (!!testValue === ( // testValue is boolean
                typeof value !== 'undefined'
                && value !== null
                && value !== ''
            ));
        },
        options: [
            { label: '', value: '' },
            { label: 'Ja', value: true },
            { label: 'Nej', value: false }
        ]
    });
}

function includes(column: ColumnType) {
    return ({
        columnKey: column.key,
        key: `${column.key}-includes`,
        label: 'Innehåller',
        applyDirectly: column.applyDirectly,
        getDescription: (testValue: string) => `${column.name} innehåller "${testValue.toLowerCase()}"`,
        test: (row: any, testValue: string) => (
            `${getRowValue({ column, row })}`
        ).toLowerCase().includes(testValue.toLowerCase())
    });
}

function containsAny(column: ColumnType) {
    // eslint-disable-next-line no-confusing-arrow
    const options = (column.options || []).map((opt) => typeof opt === 'string' ? { label: opt, value: opt } : opt);

    return ({
        columnKey: column.key,
        key: `${column.key}-containsAny`,
        label: column.name,
        applyDirectly: column.applyDirectly,
        getDescription: (testValue: string) => {
            const selected = testValue.split(',').filter(Boolean);
            const labels = selected.map((val) => options.find((opt) => opt.value === val)?.label || val);
            return `${column.name}: ${labels.join(', ')}`;
        },
        test: (row: any, testValue: string) => {
            const selectedValues = testValue.split(',').filter(Boolean);
            if (!selectedValues.length) {
                return true;
            }
            const rowValue = getRowValue({ column, row });
            if (Array.isArray(rowValue)) {
                return selectedValues.some((val) => rowValue.includes(val));
            }
            return selectedValues.includes(String(rowValue));
        },
        options,
        isMultiSelect: true,
        exclusive: column.exclusive
    });
}

function getGreaterThan({ formatValue, ...column }: ColumnType & FormatValueType) {
    return ({
        columnKey: column.key,
        key: `${column.key}-greaterThan`,
        label: 'Större än',
        applyDirectly: column.applyDirectly,
        getDescription: (testValue: string) => `${column.name} > ${formatValue(testValue)}`,
        test: (row: any, testValue: string) => (
            formatValue(getRowValue({ column, row })) > formatValue(testValue)
        )
    });
}

function getLessThan({ formatValue, ...column }: ColumnType & FormatValueType) {
    return ({
        columnKey: column.key,
        key: `${column.key}-lessThan`,
        label: 'Mindre än',
        applyDirectly: column.applyDirectly,
        getDescription: (testValue: string) => `${column.name} < ${formatValue(testValue)}`,
        test: (row: any, testValue: string) => (
            formatValue(getRowValue({ column, row })) < formatValue(testValue)
        )
    });
}

// BASE TESTS
// function empty(testValue) {
//     return testValue === null || testValue === '';
// }

// -- function notEmpty(testValue) {
// --     return !empty(testValue);
// -- }

// -- function notEquals(testValue, cmpValue) {
// --     return !equals(testValue);
// -- }

// function greaterThan(testValue, cmpValue) {
//     return testValue > cmpValue;
// }

// function lessThan(testValue, cmpValue) {
//     return testValue < cmpValue;
// }
/*
returns
*/
function dateKey(value: unknown): string | null {
    const formatted = formatDate(value);
    return formatted === '-' ? null : formatted;
}

function publicationDateEquals(column: ColumnType) {
    const section = column.filterSectionLabel || column.name;
    return ({
        columnKey: column.key,
        key: `${column.key}-equals`,
        label: 'Är lika med',
        applyDirectly: column.applyDirectly,
        getDescription: (testValue: string) => `${section} är ${formatDate(testValue)}`,
        test: (row: any, testValue: string) => {
            if (row.status !== 'published') {
                return true;
            }
            const rowDate = dateKey(row[column.key]);
            const cmp = dateKey(testValue);
            return Boolean(rowDate && cmp && rowDate === cmp);
        }
    });
}

function publicationDateFrom(column: ColumnType) {
    const section = column.filterSectionLabel || column.name;
    return ({
        columnKey: column.key,
        key: `${column.key}-from`,
        label: 'Fr.o.m.',
        applyDirectly: column.applyDirectly,
        getDescription: (testValue: string) => `${section} fr.o.m. ${formatDate(testValue)}`,
        test: (row: any, testValue: string) => {
            if (row.status !== 'published') {
                return true;
            }
            const rowDate = dateKey(row[column.key]);
            const cmp = dateKey(testValue);
            return Boolean(rowDate && cmp && rowDate >= cmp);
        }
    });
}

function publicationDateTo(column: ColumnType) {
    const section = column.filterSectionLabel || column.name;
    return ({
        columnKey: column.key,
        key: `${column.key}-to`,
        label: 'T.o.m.',
        applyDirectly: column.applyDirectly,
        getDescription: (testValue: string) => `${section} t.o.m. ${formatDate(testValue)}`,
        test: (row: any, testValue: string) => {
            if (row.status !== 'published') {
                return true;
            }
            const rowDate = dateKey(row[column.key]);
            const cmp = dateKey(testValue);
            return Boolean(rowDate && cmp && rowDate <= cmp);
        }
    });
}

export default function getTableFiltersByKey(column: ColumnType) {
    if (column.filterType === 'publicationDate') {
        return [
            publicationDateEquals(column),
            publicationDateFrom(column),
            publicationDateTo(column)
        ];
    }
    switch (column.type) {
        case 'multi-select':
            return [
                containsAny(column)
            ];
        case 'bool':
            return [
                equals(column)
            ];
        case 'number':
            return [
                getGreaterThan({
                    ...column,
                    formatValue: parseFloat
                }),
                getLessThan({
                    ...column,
                    formatValue: parseFloat
                }),
                hasValue(column)
                // notEquals,
                // isEmpty,
                // isNotEmpty
            ];
        case 'date':
            return [
                getGreaterThan({
                    ...column,
                    formatValue: formatDate
                }),
                getLessThan({
                    ...column,
                    formatValue: formatDate
                }),
                hasValue(column)
                // notEquals,
                // isEmpty,
                // isNotEmpty
            ];
        case 'datetime':
            return [
                getGreaterThan({
                    ...column,
                    formatValue: formatDateTime
                }),
                getLessThan({
                    ...column,
                    formatValue: formatDateTime
                }),
                hasValue(column)
                // notEquals,
                // isEmpty,
                // isNotEmpty
            ];
        case 'string':
        default:
            // Column.options → single dropdown equals (e.g. status)
            if (column.options?.length) {
                return [equals(column)];
            }
            return [
                includes(column),
                hasValue(column)
                // contains,
                // notContains,
                // isEmpty
                // isNotEmpty
            ];
    }
}
